import CoPresentIcon from '@mui/icons-material/CoPresent';
import EmailIcon from '@mui/icons-material/Email';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FacebookIcon from '@mui/icons-material/Facebook';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import IosShareIcon from '@mui/icons-material/IosShare';
import LanguageIcon from '@mui/icons-material/Language';
import ListAltIcon from '@mui/icons-material/ListAlt';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import MessageIcon from '@mui/icons-material/Message';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import QueryBuilderIcon from '@mui/icons-material/QueryBuilder';
import SortByAlphaIcon from '@mui/icons-material/SortByAlpha';
import StarIcon from '@mui/icons-material/Star';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import TodayIcon from '@mui/icons-material/Today';
import WebIcon from '@mui/icons-material/Web';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    FormGroup,
    Grid,
    IconButton,
    ListItemIcon,
    ListItemText,
    MenuItem,
    MenuList,
    Stack,
    Switch,
    TextField,
    Theme,
    Tooltip,
    Typography,
} from '@mui/material';
import React from 'react';
import { makeStyles } from 'tss-react/mui';
import useDebounce from '../../../hooks/useDebounce';
import { useNewRecent } from '../../../sic-lib/api/hooks/useNewRecent';
import { useCreateSavedFilter } from '../../../sic-lib/api/hooks/useSavedFilters';
import {
    CHANNEL_TYPE_LABELS,
    INBOX_DATE_RANGE_LABELS,
    INBOX_DATE_RANGE_TYPES,
    INBOX_DEFAULT_SORT,
    INBOX_FILTER_INITIAL_STATE,
    INBOX_FILTERABLE_CHANNEL_TYPES,
    INBOX_FILTERABLE_DATE_RANGE,
    INBOX_OWNERSHIP_FILTERS_ADMIN,
    INBOX_OWNERSHIP_FILTERS_NON_ADMIN,
    OWNERSHIP_LABELS,
} from '../../../sic-lib/defaults';
import { ChannelType } from '../../../sic-lib/enums/ChannelType';
import { GenericReducer, genericReducer } from '../../../sic-lib/reducers/genericReducer';
import { InboxFilter } from '../../../sic-lib/types/InboxTypes';
import { DateRangeType } from '../../../sic-lib/types/SortListFilterItem';
import { useAppDispatch } from '../../../store/hooks';
import { setFilters } from '../../../store/inbox';
import { useUserIsAdmin } from '../../../store/users';
import { DrawerCommon } from '../../Shared/Drawer/DrawerCommon';
import ToggleComponents from '../../Shared/ToggleComponents/ToggleComponents';
import NewRecentFilterDescription from './NewRecentFilterDescription/NewRecentFilterDescription';
import NewRecentFilterOptionsSearchBar from './NewRecentFilterOptionsSearchBar/NewRecentFilterOptionsSearchBar';

const getChannelIcon = (channel: ChannelType) => {
    let channelIcon;
    switch (channel) {
        case ChannelType.callEvents:
            channelIcon = <LocalPhoneIcon color="action" />;
            break;
        case ChannelType.gmailEvents:
            channelIcon = <EmailIcon color="action" />;
            break;
        case ChannelType.messageEvents:
            channelIcon = <MessageIcon color="action" />;
            break;
        case ChannelType.facebookEvents:
            channelIcon = <FacebookIcon color="action" />;
            break;
        case ChannelType.listingEvents:
            channelIcon = <ListAltIcon color="action" />;
            break;
        case ChannelType.ePlans:
            channelIcon = <TodayIcon color="action" />;
            break;
        case ChannelType.webEvents:
            channelIcon = <WebIcon color="action" />;
            break;
        case ChannelType.formRequests:
            channelIcon = <IosShareIcon color="action" />;
            break;
        case ChannelType.externalRequests:
            channelIcon = <CoPresentIcon color="action" />;
            break;
        case ChannelType.actionPlans:
            channelIcon = <PendingActionsIcon color="action" />;
            break;
        default:
            channelIcon = <LanguageIcon color="action" />;
    }
    return channelIcon;
};

interface Props {
    isOpen: boolean;
    setShowFilterDrawer: React.Dispatch<React.SetStateAction<boolean>>;
    setShowSavedFiltersDrawer: React.Dispatch<React.SetStateAction<boolean>>;
    currentInboxFilter: InboxFilter;
    appliedFilterFlags: {
        isDefault: boolean;
        isCurrentInitialState: boolean;
        isCurrentDirty: boolean;
    };
}

const NewRecentFilterDrawer = ({
    currentInboxFilter,
    isOpen,
    setShowFilterDrawer,
    setShowSavedFiltersDrawer,
    appliedFilterFlags,
}: Props) => {
    const { classes } = useStyles();
    const dispatch = useAppDispatch();
    const [openConfirmDialog, setOpenConfirmDialog] = React.useState(false);
    const [mainAccordionExpanded, setMainAccordionExpanded] = React.useState<string | false>(false);
    const [subAccordionExpanded, setSubAccordionExpanded] = React.useState<string | false>(false);
    const [dateFilters, setDateFilters] = React.useState<DateRangeType[]>(INBOX_DATE_RANGE_TYPES);
    const { mutateAsync: createSavedFilter, isSuccess } = useCreateSavedFilter();
    const { mutateAsync: updateSavedFilter } = useCreateSavedFilter();
    const [compState, setCompState] = React.useReducer<GenericReducer<InboxFilter>>(
        genericReducer,
        currentInboxFilter,
    );
    const debounceCompState = useDebounce(compState, 200);
    const { data } = useNewRecent(currentInboxFilter.filter);
    const isUserAdmin = useUserIsAdmin();

    //reopening the drawer causes the compState to be refreshed
    React.useEffect(() => {
        if (isOpen) setCompState(currentInboxFilter);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    React.useEffect(() => {
        dispatch(setFilters({ filter: debounceCompState }));
    }, [debounceCompState, dispatch]);

    const handleSaveAs = () => {
        setOpenConfirmDialog(true);
    };
    const handleClose = () => {
        setOpenConfirmDialog(false);
    };

    //Accordion Functions
    const handleMainAccordionExpand =
        (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
            setMainAccordionExpanded(isExpanded ? panel : false);
        };
    const handleSubAccordionExpand =
        (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
            setSubAccordionExpanded(isExpanded ? panel : false);
        };

    const ownershipFilters = React.useMemo(() => {
        return isUserAdmin ? INBOX_OWNERSHIP_FILTERS_ADMIN : INBOX_OWNERSHIP_FILTERS_NON_ADMIN;
    }, [isUserAdmin]);

    const handleChannelToggle = (channel: ChannelType) => {
        //adding a new channel
        if (compState.filter.channels.indexOf(channel) === -1) {
            setCompState({
                filter: {
                    ...compState.filter,
                    channels: [...compState.filter.channels, channel],
                },
            });
        }
        //removing a channel
        else {
            setCompState({
                filter: {
                    ...compState.filter,
                    channels: compState.filter.channels.filter(ch => ch !== channel),
                },
            });
        }
    };

    const handleFieldChanged = (label: string, value: any) => {
        setCompState({
            filter: {
                ...compState.filter,
                [label]: value,
            },
        });
    };
    //Accordion Functions END

    const handleClearFilter = () => {
        setCompState(INBOX_FILTER_INITIAL_STATE);
        setDateFilters(
            dateFilters.map(itm => {
                return { ...itm, isActive: false };
            }),
        );
    };

    const handleCurrentFilterUpdate = async () => {
        try {
            await updateSavedFilter(compState);
        } catch (error: any) {
            console.log('updateSavedFilter ERROR:', error);
        }
    };

    const handleSaveNewFilter = async () => {
        try {
            const newFilter = await createSavedFilter(compState);

            dispatch(setFilters({ filter: newFilter }));

            handleClose();
        } catch (error: any) {
            console.log('createSavedFilter ERROR:', error);
        }
    };

    return (
        <>
            <DrawerCommon
                open={isOpen}
                handleOnClose={() => setShowFilterDrawer(false)}
                title="Filter New/Recent"
                width="700px"
                anchor="left"
                titleNodes={
                    <>
                        <NewRecentFilterDescription />
                        {data?.pages[0].totalContacts && (
                            <Typography
                                textAlign="right"
                                sx={{
                                    fontSize: '.8rem',
                                }}
                            >{`Found ${data?.pages[0].totalContacts} Contacts`}</Typography>
                        )}
                    </>
                }
                footer={
                    <Stack
                        direction="row"
                        p={2}
                        spacing={2}
                        alignItems="center"
                        justifyContent="space-between"
                    >
                        <Stack direction="row" spacing={2} alignItems="center">
                            <Button
                                variant="outlined"
                                color="primary"
                                onClick={handleClearFilter}
                                disabled={appliedFilterFlags.isCurrentInitialState}
                            >
                                Clear
                            </Button>
                        </Stack>
                        <Stack
                            direction="row"
                            spacing={2}
                            alignItems="center"
                            flexWrap="wrap"
                            useFlexGap
                        >
                            {compState.id !== null ? (
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    onClick={handleCurrentFilterUpdate}
                                    disabled={!appliedFilterFlags.isCurrentDirty}
                                >
                                    Update
                                </Button>
                            ) : (
                                <Button variant="outlined" color="secondary" onClick={handleSaveAs}>
                                    Save As
                                </Button>
                            )}
                            <Button
                                variant="outlined"
                                onClick={() => {
                                    setShowSavedFiltersDrawer(true);
                                    setShowFilterDrawer(false);
                                }}
                            >
                                Saved Filters
                            </Button>
                            <Button variant="outlined" onClick={() => setShowFilterDrawer(false)}>
                                View Filter
                            </Button>
                        </Stack>
                    </Stack>
                }
            >
                <Stack>
                    <Stack direction="row" spacing={1} p={1} alignItems="center">
                        <IconButton
                            onClick={() =>
                                setCompState({
                                    isDefault: !compState.isDefault,
                                })
                            }
                            size="large"
                        >
                            <Tooltip
                                title={
                                    compState.isDefault
                                        ? 'Remove Default Filter'
                                        : 'Set Filter as Default'
                                }
                            >
                                <ListItemIcon className={classes.starIcon}>
                                    {currentInboxFilter.isDefault ? (
                                        <StarIcon />
                                    ) : (
                                        <StarOutlineIcon />
                                    )}
                                </ListItemIcon>
                            </Tooltip>
                        </IconButton>
                        <ToggleComponents>
                            {(toggle, onToggleHandler) =>
                                !toggle ? (
                                    <Tooltip title="Edit">
                                        <Grid
                                            item
                                            onClick={onToggleHandler}
                                            className={classes.compName}
                                        >
                                            <Grid item>
                                                <Typography
                                                    className={classes.filterName}
                                                    variant="h6"
                                                    sx={{
                                                        opacity: compState.name !== '' ? 1 : 0.5,
                                                    }}
                                                >
                                                    {compState.name !== ''
                                                        ? compState.name
                                                        : 'Enter a Filter Name'}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Tooltip>
                                ) : (
                                    <Grid item className={classes.compNameInput}>
                                        <TextField
                                            onKeyUp={event => {
                                                if (event.key === 'Enter') onToggleHandler();
                                            }}
                                            onBlur={onToggleHandler}
                                            defaultValue={
                                                compState.name !== ''
                                                    ? compState.name
                                                    : 'Enter a Filter Name'
                                            }
                                            autoFocus
                                            onChange={e =>
                                                setCompState({
                                                    name: e.currentTarget.value,
                                                })
                                            }
                                        />
                                    </Grid>
                                )
                            }
                        </ToggleComponents>
                    </Stack>
                    <NewRecentFilterOptionsSearchBar
                        compState={compState}
                        setCompState={setCompState}
                    />
                    <Grid item container direction="column">
                        <Accordion
                            disableGutters
                            expanded={mainAccordionExpanded === 'panel1'}
                            onChange={handleMainAccordionExpand('panel1')}
                            className={classes.mainAccordions}
                            sx={{ '&:before': { backgroundColor: '#fff' } }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                aria-controls="panel1a-content"
                                id="panel1a-header"
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    <QueryBuilderIcon
                                        color="action"
                                        style={{ marginRight: '1rem' }}
                                    />
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                        }}
                                    >
                                        <Typography variant="h6">Date</Typography>
                                        <Typography
                                            sx={{
                                                color: 'text.secondary',
                                                fontSize: '.7rem',
                                            }}
                                        >{`Activity Date: ${
                                            INBOX_DATE_RANGE_LABELS[compState.filter.activityRange]
                                        }, Registration Date: ${
                                            INBOX_DATE_RANGE_LABELS[
                                                compState.filter.registrationRange
                                            ]
                                        }, Channel Date: ${
                                            INBOX_DATE_RANGE_LABELS[compState.filter.channelsRange]
                                        }`}</Typography>
                                    </div>
                                </div>
                            </AccordionSummary>
                            <AccordionDetails
                                sx={{
                                    '&.MuiAccordionDetails-root': {
                                        padding: 0,
                                    },
                                }}
                            >
                                {dateFilters.map(itm => (
                                    <Accordion
                                        key={itm.label}
                                        disableGutters
                                        expanded={subAccordionExpanded === itm.label}
                                        onChange={handleSubAccordionExpand(itm.label)}
                                        square={true}
                                    >
                                        <AccordionSummary
                                            expandIcon={<ExpandMoreIcon />}
                                            aria-controls={`${itm.label}-content`}
                                            id={`${itm.label}-header`}
                                            className={classes.subAccordions}
                                        >
                                            <ListItemText inset>{itm.label}</ListItemText>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <MenuList dense>
                                                {INBOX_FILTERABLE_DATE_RANGE.map(dateItm => (
                                                    <MenuItem
                                                        key={dateItm}
                                                        onClick={() =>
                                                            handleFieldChanged(itm.value, dateItm)
                                                        }
                                                        selected={
                                                            (itm.value === 'registrationRange' &&
                                                                dateItm ===
                                                                    compState.filter
                                                                        .registrationRange) ||
                                                            (itm.value === 'activityRange' &&
                                                                dateItm ===
                                                                    compState.filter
                                                                        .activityRange) ||
                                                            (itm.value === 'channelsRange' &&
                                                                dateItm ===
                                                                    compState.filter.channelsRange)
                                                        }
                                                    >
                                                        <ListItemText inset>
                                                            {' '}
                                                            {INBOX_DATE_RANGE_LABELS[dateItm]}
                                                        </ListItemText>
                                                    </MenuItem>
                                                ))}
                                            </MenuList>
                                        </AccordionDetails>
                                    </Accordion>
                                ))}
                            </AccordionDetails>
                        </Accordion>

                        {/* channels */}
                        <Accordion
                            disableGutters
                            expanded={mainAccordionExpanded === 'panel2'}
                            onChange={handleMainAccordionExpand('panel2')}
                            className={classes.mainAccordions}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                aria-controls="panel2a-content"
                                id="panel2a-header"
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    <FilterAltIcon color="action" style={{ marginRight: '1rem' }} />
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                        }}
                                    >
                                        <Typography variant="h6">Channels</Typography>
                                        <Typography
                                            sx={{
                                                color: 'text.secondary',
                                                fontSize: '.7rem',
                                            }}
                                        >
                                            {compState.filter.channels
                                                .map(itm => CHANNEL_TYPE_LABELS[itm])
                                                .join(', ')}
                                        </Typography>
                                    </div>
                                </div>
                            </AccordionSummary>
                            <AccordionDetails>
                                <MenuList dense>
                                    {INBOX_FILTERABLE_CHANNEL_TYPES.map(option => (
                                        <MenuItem
                                            title={CHANNEL_TYPE_LABELS[option]}
                                            key={CHANNEL_TYPE_LABELS[option]}
                                            onClick={() => handleChannelToggle(option)}
                                        >
                                            {getChannelIcon(option)}
                                            <ListItemText inset>
                                                {CHANNEL_TYPE_LABELS[option]}
                                            </ListItemText>
                                            <Switch
                                                checked={compState.filter.channels.includes(option)}
                                                onChange={() => handleChannelToggle(option)}
                                            />
                                        </MenuItem>
                                    ))}
                                </MenuList>
                            </AccordionDetails>
                        </Accordion>

                        {/* sort */}
                        <Accordion
                            disableGutters
                            expanded={mainAccordionExpanded === 'panel3'}
                            onChange={handleMainAccordionExpand('panel3')}
                            className={classes.mainAccordions}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                aria-controls="panel3a-content"
                                id="panel3a-header"
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    <SortByAlphaIcon
                                        color="action"
                                        style={{ marginRight: '1rem' }}
                                    />
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                        }}
                                    >
                                        <Typography variant="h6">Sort Order</Typography>
                                        <Typography
                                            sx={{
                                                color: 'text.secondary',
                                                fontSize: '.7rem',
                                            }}
                                        >
                                            {
                                                INBOX_DEFAULT_SORT.find(
                                                    itm =>
                                                        itm.sortItem.field ===
                                                            compState.filter.sort.field &&
                                                        itm.sortItem.direction ===
                                                            compState.filter.sort.direction,
                                                )?.label
                                            }
                                        </Typography>
                                    </div>
                                </div>
                            </AccordionSummary>
                            <AccordionDetails>
                                <MenuList dense>
                                    {INBOX_DEFAULT_SORT.map(option => (
                                        <MenuItem
                                            title={option.label}
                                            key={option.label}
                                            onClick={() =>
                                                handleFieldChanged('sort', option.sortItem)
                                            }
                                            selected={
                                                option.sortItem.field ===
                                                    compState.filter.sort.field &&
                                                option.sortItem.direction ===
                                                    compState.filter.sort.direction
                                            }
                                        >
                                            <ListItemText inset>{option.label}</ListItemText>
                                        </MenuItem>
                                    ))}
                                </MenuList>
                            </AccordionDetails>
                        </Accordion>

                        {/* OwnershipScope */}
                        <Accordion
                            disableGutters
                            expanded={mainAccordionExpanded === 'panel4'}
                            onChange={handleMainAccordionExpand('panel4')}
                            className={classes.mainAccordions}
                            style={{ marginBottom: '1rem' }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                aria-controls="panel4a-content"
                                id="panel4a-header"
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    <SupervisorAccountIcon
                                        color="action"
                                        style={{ marginRight: '1rem' }}
                                    />
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                        }}
                                    >
                                        <Typography variant="h6">Ownership Scope</Typography>
                                        <Typography
                                            sx={{
                                                color: 'text.secondary',
                                                fontSize: '.7rem',
                                            }}
                                        >
                                            {OWNERSHIP_LABELS[compState.filter.ownershipScope]}
                                        </Typography>
                                    </div>
                                </div>
                            </AccordionSummary>
                            <AccordionDetails>
                                <MenuList dense>
                                    {ownershipFilters.map(option => (
                                        <MenuItem
                                            title={OWNERSHIP_LABELS[option]}
                                            key={option}
                                            onClick={() =>
                                                handleFieldChanged('ownershipScope', option)
                                            }
                                            selected={option === compState.filter.ownershipScope}
                                        >
                                            <ListItemText inset>
                                                {OWNERSHIP_LABELS[option]}
                                            </ListItemText>
                                        </MenuItem>
                                    ))}
                                </MenuList>
                            </AccordionDetails>
                        </Accordion>
                    </Grid>
                </Stack>
            </DrawerCommon>
            {/* Dialog Create New Filter */}
            <Dialog onClose={handleClose} open={openConfirmDialog}>
                <DialogTitle>Provide New Filter Name</DialogTitle>
                <DialogContent>
                    <FormGroup>
                        <TextField
                            autoFocus
                            id="text_create_filter"
                            name="createFilter"
                            defaultValue={compState.name}
                            onChange={e => setCompState({ name: e.currentTarget.value })}
                            style={{ marginBottom: '1rem' }}
                        />

                        <FormControlLabel
                            control={<Checkbox />}
                            label="Default Filter"
                            onChange={() =>
                                setCompState({
                                    isDefault: !compState.isDefault,
                                })
                            }
                            checked={compState.isDefault}
                        />
                    </FormGroup>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button onClick={handleSaveNewFilter} disabled={compState.name === ''}>
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

const useStyles = makeStyles()((theme: Theme) => ({
    gridHeader: {
        paddingLeft: theme.spacing(1),
    },
    compName: {
        cursor: 'pointer',
        paddingLeft: theme.spacing(1),
    },
    compNameInput: {
        paddingLeft: theme.spacing(1),
    },
    totalContacts: {
        marginRight: theme.spacing(6),
    },
    starIcon: {
        minWidth: 0,
        color: 'gold',
    },
    filterName: {
        maxWidth: '12rem',
        wordWrap: 'break-word',
    },
    btnGroup: {},
    mainAccordions: {
        alignItems: 'center',
    },
    subAccordions: {
        paddingLeft: '0',
    },
}));

export default NewRecentFilterDrawer;
